import AppDownloadSection from '../../components/AppDownloadSection/AppDownloadSection';
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaTimes, FaChevronLeft, FaChevronRight, FaExpand } from 'react-icons/fa';

// ── Downloaded images ───────────────────────────────────────
import kitchen1 from '../../assets/images/gallery-kitchen-1.jpg';
import kitchen2 from '../../assets/images/gallery-kitchen-2.jpg';
import kitchen3 from '../../assets/images/gallery-kitchen-3.jpg';
import eventRun from '../../assets/images/gallery-event-run.jpg';
import eventHoli from '../../assets/images/gallery-event-holi.jpg';
import eventBanner from '../../assets/images/gallery-event-banner.jpg';
import eventComm1 from '../../assets/images/gallery-event-community-1.jpg';
import eventComm2 from '../../assets/images/gallery-event-community-2.jpg';

// ── Existing project images reused in gallery ───────────────
import dosa from '../../assets/images/masala-dosa.jpg';
import paratha from '../../assets/images/methi-paratha.jpg';
import thali from '../../assets/images/south-thali-menu.png';
import zeera from '../../assets/images/zeera-rice-dal.jpg';
import friedRiceRaita from '../../assets/images/fried-rice-raita.png';
import miniThaliDelivery from '../../assets/images/mini-thali-v3.png';
import ecoPackBox from '../../assets/images/eco-design-transparent-v3.png';

import './Gallery.css';

// ── Event groups ─────────────────────────────────────────────
const groups = [
  {
    id: 'kitchen',
    label: 'Behind the Scenes',
    color: '#1565c0',
    images: [
      { src: kitchen1, caption: 'Our Cloud Kitchen' },
      { src: kitchen2, caption: 'Chef at Work' },
      { src: kitchen3, caption: 'Food Preparation' },
    ],
  },
  {
    id: 'events',
    label: 'Events & Community',
    color: '#c62828',
    images: [
      { src: eventComm1, caption: 'Community Food Drive – Mythrinagar' },
      { src: eventComm2, caption: 'Digirasa Team Serving at Community Event' },
      { src: eventRun, caption: 'Digirasa at I AM A FINISHER Run' },
      { src: eventBanner, caption: 'Health With Every Subscription – Display' },
      { src: eventHoli, caption: 'Digirasa Holi Special Wishes' },
    ],
  },
  {
    id: 'food',
    label: 'Our Food',
    color: 'var(--primary)',
    images: [
      { src: dosa, caption: 'Masala Dosa' },
      { src: paratha, caption: 'Methi Paratha' },
      { src: thali, caption: 'South India Thali' },
      { src: zeera, caption: 'Zeera Rice & Dal Tadka' },
    ],
  },
  {
    id: 'delivery',
    label: 'Meal Delivery & Packaging',
    color: '#7b1fa2',
    images: [
      { src: miniThaliDelivery, caption: 'Mini Thali Delivery Pack' },
      { src: ecoPackBox, caption: 'Eco-friendly Delivery Packaging' },
      { src: friedRiceRaita, caption: 'Ready-to-Deliver Meal Box' },
      { src: paratha, caption: 'Methi Paratha Pack' },
      { src: thali, caption: 'South India Thali Box' },
    ],
  },
];

// Flatten all images with their group index + local index for lightbox navigation
const allImages = groups.flatMap((g, gi) =>
  g.images.map((img, li) => ({ ...img, groupId: g.id, groupLabel: g.label, gi, li })),
);

const Gallery = () => {
  const [activeGroup, setActiveGroup] = useState('all');
  const [lightbox, setLightbox] = useState(null); // index into allImages

  // Filtered flat list for current tab
  const visibleImages =
    activeGroup === 'all' ? allImages : allImages.filter((img) => img.groupId === activeGroup);

  const openLightbox = (img) => {
    const idx = visibleImages.findIndex(
      (v) => v.src === img.src && v.groupId === img.groupId && v.li === img.li,
    );
    setLightbox(idx);
  };

  const closeLightbox = () => setLightbox(null);

  const prev = useCallback(() => {
    setLightbox((i) => (i - 1 + visibleImages.length) % visibleImages.length);
  }, [visibleImages.length]);

  const next = useCallback(() => {
    setLightbox((i) => (i + 1) % visibleImages.length);
  }, [visibleImages.length]);

  // Keyboard navigation
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, prev, next]);

  // Lock body scroll when lightbox open
  useEffect(() => {
    document.body.style.overflow = lightbox !== null ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightbox]);

  const currentImg = lightbox !== null ? visibleImages[lightbox] : null;

  return (
    <div className="gallery-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <div className="container">
            <h1>GALLERY</h1>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Gallery</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <section className="gallery-section">
        <div className="container">
          <div className="gallery-filters">
            <button
              className={`gallery-filter-btn${activeGroup === 'all' ? ' gallery-filter-btn--active' : ''}`}
              onClick={() => setActiveGroup('all')}
            >
              All
            </button>
            {groups.map((g) => (
              <button
                key={g.id}
                className={`gallery-filter-btn${activeGroup === g.id ? ' gallery-filter-btn--active' : ''}`}
                style={activeGroup === g.id ? { background: g.color, borderColor: g.color } : {}}
                onClick={() => setActiveGroup(g.id)}
              >
                {g.label}
              </button>
            ))}
          </div>

          {/* Grouped display */}
          {activeGroup === 'all' ? (
            groups.map((g) => (
              <div key={g.id} className="gallery-group">
                <div className="gallery-group__header" style={{ borderColor: g.color }}>
                  <span className="gallery-group__dot" style={{ background: g.color }} />
                  <h2 className="gallery-group__title">{g.label}</h2>
                  <span className="gallery-group__count">{g.images.length} photos</span>
                </div>
                <div className="gallery-grid">
                  {g.images.map((img, li) => {
                    const flatImg = {
                      ...img,
                      groupId: g.id,
                      groupLabel: g.label,
                      gi: groups.indexOf(g),
                      li,
                    };
                    return (
                      <div key={li} className="gallery-item" onClick={() => openLightbox(flatImg)}>
                        <img src={img.src} alt={img.caption} loading="lazy" />
                        <div className="gallery-item__overlay">
                          <FaExpand className="gallery-item__icon" />
                          <p>{img.caption}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <div className="gallery-grid gallery-grid--flat">
              {visibleImages.map((img, i) => (
                <div key={i} className="gallery-item" onClick={() => openLightbox(img)}>
                  <img src={img.src} alt={img.caption} loading="lazy" />
                  <div className="gallery-item__overlay">
                    <FaExpand className="gallery-item__icon" />
                    <p>{img.caption}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <AppDownloadSection />

      {/* Lightbox */}
      {lightbox !== null && currentImg && (
        <div className="lightbox" onClick={closeLightbox}>
          <button className="lightbox__close" onClick={closeLightbox} aria-label="Close">
            <FaTimes />
          </button>

          <button
            className="lightbox__arrow lightbox__arrow--prev"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            aria-label="Previous"
          >
            <FaChevronLeft />
          </button>

          <div className="lightbox__content" onClick={(e) => e.stopPropagation()}>
            <img src={currentImg.src} alt={currentImg.caption} className="lightbox__img" />
            <div className="lightbox__caption">
              <p className="lightbox__caption-text">{currentImg.caption}</p>
              <span className="lightbox__caption-group">{currentImg.groupLabel}</span>
              <span className="lightbox__counter">
                {lightbox + 1} / {visibleImages.length}
              </span>
            </div>
          </div>

          <button
            className="lightbox__arrow lightbox__arrow--next"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            aria-label="Next"
          >
            <FaChevronRight />
          </button>

          {/* Thumbnail strip */}
          <div className="lightbox__thumbs" onClick={(e) => e.stopPropagation()}>
            {visibleImages.map((img, i) => (
              <div
                key={i}
                className={`lightbox__thumb${i === lightbox ? ' lightbox__thumb--active' : ''}`}
                onClick={() => setLightbox(i)}
              >
                <img src={img.src} alt={img.caption} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
